import { useState, useCallback, memo } from "react";

import cc from "classcat";
import { useLocalization } from "@fluent/react";
import { Button, IconButton, Popover, Tooltip } from "@narmi/design_system";
import { useFeature } from "byzantine";

import { useIsSidebarEnabled, useSidebarContainer } from "../sidebar.hooks";
import SearchBar from "../../search/SearchBar";

import styles from "./SidebarTop.module.scss";

const SidebarTop = () => {
  const { isSidebarEnabled } = useIsSidebarEnabled();
  const { isCollapsed } = useSidebarContainer();
  const isSidebarExpanded = isSidebarEnabled && !isCollapsed;
  const { l10n } = useLocalization();
  const { featureEnabled } = useFeature();
  const [isLogoutMenuOpen, setIsLogoutMenuOpen] = useState(false);

  const closeLogoutMenu = useCallback(() => {
    setIsLogoutMenuOpen(false);
  }, []);

  const handleTransferClick = useCallback(() => {
    window.location.assign("/transfer/");
  }, []);

  const handleSupportClick = useCallback(() => {
    window.location.assign("/support");
  }, []);

  const handleSettingsClick = useCallback(() => {
    window.location.assign("/settings");
  }, []);

  const handleLogoutClick = useCallback(() => {
    window.location.assign("/logout");
  }, []);

  // NDS Popover's built-in trigger (role="button" div) only toggles on Enter,
  // so pressing Space on it falls through to the browser default (page scroll).
  // The keydown fires on Popover's trigger div, so we intercept it as it
  // bubbles up through this wrapper. Only act while closed (when open, focus is
  // inside the menu and Space should activate the focused menu item instead).
  const handleTriggerKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      const target = e.target as HTMLElement;
      if (
        (e.key === " " || e.key === "Spacebar") &&
        !isLogoutMenuOpen &&
        target.classList.contains("nds-popover-trigger")
      ) {
        e.preventDefault();
        setIsLogoutMenuOpen(true);
      }
    },
    [isLogoutMenuOpen]
  );

  if (!isSidebarEnabled) {
    return null;
  }

  return (
    <div
      className={cc([
        styles.topRow,
        { [styles.sidebarExpanded]: isSidebarExpanded },
      ])}
    >
      <div className={styles.content}>
        <SearchBar />
        <div className={styles.actions}>
          {featureEnabled("transfers") && (
            <Button
              size="m"
              label={l10n.getString("button-transfer", null, "Transfer funds")}
              onClick={handleTransferClick}
            />
          )}
          <div className={styles.iconButtons}>
            <Tooltip side="bottom" text={l10n.getString("sidebar-support")}>
              <div className={styles.iconButtonWrapper}>
                <IconButton
                  kind="plain"
                  name="life-buoy"
                  label={l10n.getString("sidebar-support")}
                  onClick={handleSupportClick}
                />
              </div>
            </Tooltip>
            <Tooltip side="bottom" text={l10n.getString("topnav-settings")}>
              <div className={styles.iconButtonWrapper}>
                <IconButton
                  kind="plain"
                  name="settings"
                  label={l10n.getString("topnav-settings", null, "Settings")}
                  onClick={handleSettingsClick}
                />
              </div>
            </Tooltip>
            <div
              className={styles.logoutTriggerWrapper}
              onKeyDown={handleTriggerKeyDown}
            >
              <Popover
                side="bottom"
                autoFocus
                isOpen={isLogoutMenuOpen}
                onUserDismiss={closeLogoutMenu}
                onUserEnable={() => setIsLogoutMenuOpen(true)}
                renderTrigger={() => (
                  <Tooltip
                    side="bottom"
                    isOpen={isLogoutMenuOpen ? false : undefined}
                    text={l10n.getString("topnav-logout")}
                  >
                    <div
                      className={`${styles.iconButtonWrapper} ${styles.iconButtonWrapperPrimary}`}
                    >
                      <IconButton
                        kind="plain"
                        name="log-out"
                        label={l10n.getString("topnav-logout")}
                        tabIndex={-1}
                      />
                    </div>
                  </Tooltip>
                )}
                content={
                  <div
                    className={styles.logoutMenu}
                    role="menu"
                    aria-label={l10n.getString("topnav-logout")}
                  >
                    <button
                      className={`${styles.logoutMenuItem} button--reset`}
                      onClick={closeLogoutMenu}
                      role="menuitem"
                      type="button"
                    >
                      {l10n.getString("button-cancel")}
                    </button>
                    <button
                      className={`${styles.logoutMenuItem} ${styles.logoutMenuItemDanger} button--reset`}
                      onClick={handleLogoutClick}
                      role="menuitem"
                      type="button"
                    >
                      {l10n.getString(
                        "sidebar-logout-confirm",
                        null,
                        "Yes, logout"
                      )}
                    </button>
                  </div>
                }
              />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

SidebarTop.displayName = "SidebarTop";

export default memo(SidebarTop);
